import { ReactElement, ReactNode } from "react";
import { FetcherWithComponents, useFetcher } from "react-router";

type OtherFormData = {
    [key: string]: unknown;
};

type QuoteFormAction = 'PreviousStep' | 'NextStep' | 'SubmitForm' | `Custom${string}`;

type QuoteFormActionInputProps = {
    action: QuoteFormAction;
    inputs?: OtherFormData;
};
  
export type QuoteFormActionInput = {
    action: QuoteFormAction;
    inputs?: OtherFormData;
};
  
export type QuoteFormProps = QuoteFormActionInputProps & {
    children: ReactNode | ((fetcher: FetcherWithComponents<any>) => ReactNode);
    route?: string;
    fetcherKey?: string;
    className?: string;
    formProps?: any;
};
  
const INPUT_NAME = 'customFormInput';
  
export function QuoteForm({
    children,
    action,
    inputs,
    route,
    fetcherKey,
    className,
    formProps
}: QuoteFormProps): ReactElement {
    const fetcher = useFetcher({ key: fetcherKey });
  
    return (
      <fetcher.Form
        method="POST"
        action={route || ''}
        className={className}
        {...formProps}
      >
        {(action || inputs) && (
          <input
            type="hidden"
            name={INPUT_NAME}
            value={JSON.stringify({ action, inputs })}
          />
        )}
        {typeof children === 'function' ? children(fetcher) : children}
      </fetcher.Form>
    );
}
  
QuoteForm.INPUT_NAME = INPUT_NAME;
QuoteForm.ACTIONS = {
    PreviousStep: 'PreviousStep',
    NextStep: 'NextStep',
    SubmitForm: 'SubmitForm',
} as const;
  
function isQuoteFormActionInput(obj: any): obj is QuoteFormActionInput {
    return (
      typeof obj === 'object' &&
      obj !== null &&
      typeof obj.action === 'string'
    );
}
  
function getFormInput(formData: FormData): QuoteFormActionInput {
    const data: Record<string, unknown> = {};
    for (const pair of formData.entries()) {
      const key = pair[0];
      const values = formData.getAll(key);
      data[key] = values.length > 1 ? values : pair[1];
    }
  
    const { customFormInput, ...otherData } = data;
    if (typeof customFormInput === 'string') {
      try {
        const parsed = JSON.parse(customFormInput);
        if (isQuoteFormActionInput(parsed)) {
          return {
            action: parsed.action,
            inputs: {
              ...parsed.inputs,
              ...otherData,
            },
          };
        }
      } catch (error) {
        console.error('Error parsing customFormInput:', error);
      }
    }
  
    return { action: 'CustomError', inputs: otherData };
}
  
QuoteForm.getFormInput = getFormInput;